/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/UsersActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Action buttons component for Users DataGrid rows.
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { Edit, Copy, Trash2 } from 'lucide-react';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { User } from '../types/users.types';
import { navigateToUserDuplicate } from '../usersActions';

interface UsersActionsProps {
  user: User;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
}

export function UsersActions({ user, onEdit, onDelete }: UsersActionsProps) {
  const { t } = useI18n();

  const handleDuplicate = useCallback(() => {
    navigateToUserDuplicate(onEdit as any, user.id);
  }, [user.id, onEdit]);

  return (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('users.gridActions.edit')} position="top">
        <button
          onClick={() => onEdit(user)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-md transition-colors"
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('users.gridActions.duplicate')} position="top">
        <button
          onClick={handleDuplicate}
          className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 rounded-md transition-colors"
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={user.isPrimaryAdmin ? t('users.tooltips.cannotDeletePrimary') : t('users.gridActions.delete')} position="top">
        <button
          onClick={() => onDelete(user)}
          disabled={user.isPrimaryAdmin}
          className={`p-2 rounded-md transition-colors ${user.isPrimaryAdmin
            ? 'text-gray-400 cursor-not-allowed opacity-50'
            : 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
            }`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </SimpleTooltip>
    </div>
  );
}

